/**
 * lifestyle-media-card.js — progressive enhancement only.
 *
 * Manages:
 * - Inline video autoplay via IntersectionObserver (one video at a time)
 * - Pause/play button state (WCAG 2.2.2)
 * - Fullscreen modal player opened by clicking anywhere on a card
 * - Re-initialization after facets.js SRA updates (#ProductGridContainer swap)
 *
 * Image-mode cards require no JS for display, but do get the modal trigger.
 * All core rendering is Liquid server-side; this file is purely additive.
 */

(function () {
  'use strict';

  var CONTAINER_SEL = '#ProductGridContainer';
  var VIDEO_SEL     = '[data-lifestyle-video]';
  var CARD_SEL      = '[data-lifestyle-media]';
  var PAUSE_SEL     = '[data-lifestyle-pause]';
  var TRIGGER_SEL   = '[data-lifestyle-trigger]';
  var ICON_PAUSE    = '.lifestyle-media-card__pause-icon';
  var ICON_PLAY     = '.lifestyle-media-card__play-icon';
  var MODAL_ID      = 'lm-modal';

  var intersectionObs = null;

  /* ── Modal state ───────────────────────────────────────────────────────── */

  var _modal       = null;   /* the shared modal DOM element */
  var _activeCard  = null;   /* the card <li> that opened the modal */
  var _inlineVideo = null;   /* the inline <video> that was playing */
  var _focusables  = [];     /* focusable elements inside modal for tab trap */

  /* ── Helpers ────────────────────────────────────────────────────────────── */

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── Inline card pause button ───────────────────────────────────────────── */

  function syncPauseBtn(video, btn) {
    var isPaused = video.paused;
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    var pauseIcon = btn.querySelector(ICON_PAUSE);
    var playIcon  = btn.querySelector(ICON_PLAY);
    if (pauseIcon) pauseIcon.hidden = isPaused;
    if (playIcon)  playIcon.hidden  = !isPaused;
  }

  function bindPauseButton(video, btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (video.paused) {
        video.play().catch(function () {});
      } else {
        video.pause();
      }
      syncPauseBtn(video, btn);
    });
    video.addEventListener('play',  function () { syncPauseBtn(video, btn); });
    video.addEventListener('pause', function () { syncPauseBtn(video, btn); });
  }

  function pauseAllExcept(container, targetVideo) {
    container.querySelectorAll(VIDEO_SEL).forEach(function (v) {
      if (v !== targetVideo && !v.paused) v.pause();
    });
  }

  /* ── Modal video controls ───────────────────────────────────────────────── */

  function syncModalPlayPause(video, btn) {
    var isPaused = video.paused;
    btn.setAttribute('aria-label', isPaused ? 'Play' : 'Pause');
    var pauseIcon = btn.querySelector('.lifestyle-modal__ctrl-pause-icon');
    var playIcon  = btn.querySelector('.lifestyle-modal__ctrl-play-icon');
    if (pauseIcon) pauseIcon.hidden = isPaused;
    if (playIcon)  playIcon.hidden  = !isPaused;
  }

  function syncModalMute(video, btn) {
    var isMuted = video.muted;
    btn.setAttribute('aria-label', isMuted ? 'Unmute' : 'Mute');
    var volOnIcon  = btn.querySelector('.lifestyle-modal__ctrl-vol-on-icon');
    var volOffIcon = btn.querySelector('.lifestyle-modal__ctrl-vol-off-icon');
    if (volOnIcon)  volOnIcon.hidden  = isMuted;
    if (volOffIcon) volOffIcon.hidden = !isMuted;
  }

  /* ── Modal ──────────────────────────────────────────────────────────────── */

  /* SVG icon paths — Material Design (MIT licence) */
  var SVG_PAUSE   = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
  var SVG_PLAY    = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>';
  var SVG_VOL_ON  = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>';
  var SVG_VOL_OFF = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>';

  function buildModal() {
    if (document.getElementById(MODAL_ID)) {
      _modal = document.getElementById(MODAL_ID);
      return;
    }

    var el = document.createElement('div');
    el.id        = MODAL_ID;
    el.className = 'lifestyle-modal';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'lm-modal-title');
    el.setAttribute('aria-hidden', 'true');

    el.innerHTML =
      '<button class="lifestyle-modal__close" type="button" aria-label="סגור">' +
        '<span aria-hidden="true">&times;</span>' +
      '</button>' +
      '<div class="lifestyle-modal__dialog">' +
        '<div class="lifestyle-modal__media">' +
          '<video class="lifestyle-modal__video" muted playsinline></video>' +
          '<img class="lifestyle-modal__image" alt="" hidden>' +
        '</div>' +
        '<div class="lifestyle-modal__controls">' +
          '<button class="lifestyle-modal__ctrl-btn lifestyle-modal__playpause" type="button" aria-label="Pause">' +
            '<span class="lifestyle-modal__ctrl-pause-icon" aria-hidden="true">' + SVG_PAUSE + '</span>' +
            '<span class="lifestyle-modal__ctrl-play-icon" aria-hidden="true" hidden>' + SVG_PLAY + '</span>' +
          '</button>' +
          '<button class="lifestyle-modal__ctrl-btn lifestyle-modal__mute" type="button" aria-label="Unmute">' +
            '<span class="lifestyle-modal__ctrl-vol-on-icon" aria-hidden="true" hidden>' + SVG_VOL_ON + '</span>' +
            '<span class="lifestyle-modal__ctrl-vol-off-icon" aria-hidden="true">' + SVG_VOL_OFF + '</span>' +
          '</button>' +
        '</div>' +
        '<div class="lifestyle-modal__info">' +
          '<p class="lifestyle-modal__title" id="lm-modal-title"></p>' +
          '<p class="lifestyle-modal__caption"></p>' +
          '<a class="lifestyle-modal__cta" href="" target="_self">צפו במוצר</a>' +
        '</div>' +
      '</div>';

    document.body.appendChild(el);
    _modal = el;

    var modalVideo   = el.querySelector('.lifestyle-modal__video');
    var playPauseBtn = el.querySelector('.lifestyle-modal__playpause');
    var muteBtn      = el.querySelector('.lifestyle-modal__mute');

    /* Clicking the dark overlay area (the modal container itself, not any child)
       closes the modal. e.target === el is true only when the click lands directly
       on the modal background — never when clicking inside the dialog or close btn. */
    el.addEventListener('click', function (e) {
      if (e.target === el) closeModal();
    });

    el.querySelector('.lifestyle-modal__close')
      .addEventListener('click', closeModal);

    playPauseBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (modalVideo.paused) {
        modalVideo.play().catch(function () {});
      } else {
        modalVideo.pause();
      }
    });

    muteBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      modalVideo.muted = !modalVideo.muted;
    });

    /* Sync control icons from video state events. */
    modalVideo.addEventListener('play',         function () { syncModalPlayPause(modalVideo, playPauseBtn); });
    modalVideo.addEventListener('pause',        function () { syncModalPlayPause(modalVideo, playPauseBtn); });
    modalVideo.addEventListener('volumechange', function () { syncModalMute(modalVideo, muteBtn); });

    document.addEventListener('keydown', function (e) {
      if (!_modal || !_modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'Tab') {
        trapFocus(e);
      }
    });
  }

  function openModal(card) {
    if (!_modal) buildModal();

    _activeCard  = card;
    _inlineVideo = card.querySelector(VIDEO_SEL);

    var modalVideo   = _modal.querySelector('.lifestyle-modal__video');
    var modalImage   = _modal.querySelector('.lifestyle-modal__image');
    var modalTitle   = _modal.querySelector('.lifestyle-modal__title');
    var modalCaption = _modal.querySelector('.lifestyle-modal__caption');
    var modalCta     = _modal.querySelector('.lifestyle-modal__cta');
    var controls     = _modal.querySelector('.lifestyle-modal__controls');
    var playPauseBtn = _modal.querySelector('.lifestyle-modal__playpause');
    var muteBtn      = _modal.querySelector('.lifestyle-modal__mute');

    /* ── Populate media ─────────────────────────────────────────────────── */

    if (_inlineVideo) {
      var targetTime = _inlineVideo.currentTime;
      var poster     = _inlineVideo.getAttribute('poster') || '';

      _inlineVideo.pause();

      modalVideo.poster = poster;
      modalVideo.src    = _inlineVideo.src;
      modalVideo.muted  = true;   /* always start muted; user can unmute via control */
      modalVideo.hidden = false;
      modalImage.hidden = true;
      modalImage.removeAttribute('src');

      if (controls) controls.hidden = false;
      /* Sync mute button to reflect muted=true before any volumechange event fires. */
      if (muteBtn) syncModalMute(modalVideo, muteBtn);

      /* Set currentTime once metadata is ready; play immediately after. */
      modalVideo.onloadedmetadata = function () {
        modalVideo.onloadedmetadata = null;
        modalVideo.currentTime = targetTime;
        modalVideo.play().catch(function () {});
        /* play event fires → syncModalPlayPause updates button to "Pause" state. */
      };
      modalVideo.load();

    } else {
      /* Image card — hide video controls entirely. */
      var cardImg = card.querySelector('.lifestyle-media-card__img');
      modalVideo.hidden = true;
      modalVideo.pause();
      modalVideo.src = '';
      if (controls) controls.hidden = true;

      if (cardImg) {
        modalImage.src    = cardImg.src;
        modalImage.alt    = cardImg.alt || '';
        modalImage.hidden = false;
      }
    }

    /* ── Populate text + CTA ────────────────────────────────────────────── */

    var title   = card.getAttribute('data-lifestyle-title')   || '';
    var caption = card.getAttribute('data-lifestyle-caption') || '';
    var link    = card.getAttribute('data-lifestyle-link')    || '';

    modalTitle.textContent   = title;
    modalCaption.textContent = caption;
    /* p:empty { display:none } in base.css — content already set above, so
       the rule won't match when the modal becomes visible. */

    if (link) {
      modalCta.href          = link;
      modalCta.style.display = '';
    } else {
      modalCta.style.display = 'none';
    }

    /* ── Show ───────────────────────────────────────────────────────────── */

    _modal.setAttribute('aria-hidden', 'false');
    _modal.classList.add('is-open');

    _focusables = [_modal.querySelector('.lifestyle-modal__close')];
    if (!modalVideo.hidden && playPauseBtn) _focusables.push(playPauseBtn);
    if (!modalVideo.hidden && muteBtn)      _focusables.push(muteBtn);
    if (link)                               _focusables.push(modalCta);

    _focusables[0].focus();
  }

  function closeModal() {
    if (!_modal) return;

    _modal.classList.remove('is-open');
    _modal.setAttribute('aria-hidden', 'true');

    var modalVideo = _modal.querySelector('.lifestyle-modal__video');
    modalVideo.pause();

    /* Resume inline video if motion is allowed. */
    if (_inlineVideo && !prefersReducedMotion()) {
      _inlineVideo.play().catch(function () {});
    }

    /* Return focus to the card's pause button, or the trigger if no pause btn. */
    if (_activeCard) {
      var returnTarget =
        _activeCard.querySelector(PAUSE_SEL) ||
        _activeCard.querySelector(TRIGGER_SEL);
      if (returnTarget) returnTarget.focus();
    }

    _activeCard  = null;
    _inlineVideo = null;
    _focusables  = [];
  }

  function trapFocus(e) {
    if (!_focusables.length) return;
    var first = _focusables[0];
    var last  = _focusables[_focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  /* ── Inline video init ──────────────────────────────────────────────────── */

  function initVideoCards() {
    /* Disconnect stale IntersectionObserver before re-init. */
    if (intersectionObs) {
      intersectionObs.disconnect();
      intersectionObs = null;
    }

    var container = document.querySelector(CONTAINER_SEL);
    if (!container) return;

    var cards = container.querySelectorAll(CARD_SEL);
    if (!cards.length) return;

    /* Bind modal trigger on every card (video or image). */
    cards.forEach(function (card) {
      var triggerBtn = card.querySelector(TRIGGER_SEL);
      if (triggerBtn) {
        triggerBtn.addEventListener('click', function () {
          openModal(card);
        });
      }
    });

    var videos = container.querySelectorAll(VIDEO_SEL);
    if (!videos.length) return;

    if (prefersReducedMotion()) {
      videos.forEach(function (v) {
        v.pause();
        v.removeAttribute('autoplay');
      });
      return;
    }

    intersectionObs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            pauseAllExcept(container, video);
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach(function (video) {
      var card = video.closest(CARD_SEL);
      var btn  = card ? card.querySelector(PAUSE_SEL) : null;
      if (btn) bindPauseButton(video, btn);
      intersectionObs.observe(video);
    });

    /* IntersectionObserver only fires on threshold crossings, so videos already
       in the viewport at init time never receive an entry. Play them immediately. */
    videos.forEach(function (video) {
      var rect = video.getBoundingClientRect();
      var inView =
        rect.top    < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left   < window.innerWidth  &&
        rect.right  > 0;
      if (inView) {
        pauseAllExcept(container, video);
        video.play().catch(function () {});
      }
    });
  }

  function init() {
    buildModal();
    initVideoCards();

    var container = document.querySelector(CONTAINER_SEL);
    if (!container) return;

    /*
     * facets.js replaces #ProductGridContainer children via innerHTML after
     * every filter/sort SRA fetch and dispatches no custom event. A
     * MutationObserver on childList is the only reliable hook.
     */
    var mutationObs = new MutationObserver(function () {
      initVideoCards();
    });

    mutationObs.observe(container, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
